import { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import axios from "axios";
import { User } from "@contractool/schema";

import { AuthContext } from "../auth";
import { EnvironmentContext } from "../environment";

interface LoginFormData {
  email: string;
  password: string;
  web: string;
}

interface LoginFormErrors {
  [key: string]: string;
}

const DEFAULT_FORM_DATA: LoginFormData = {
  email: "",
  password: "",
  web: "",
};

const ERROR_MESSAGES = {
  NETWORK:
    "Network error. Please check your internet connection and try again.",
  401: "Invalid email or password. Please check your credentials and try again.",
  404: "Login service not found. Please check the app URL.",
  422: "Please check the form data and try again.",
  500: "Server error. Please try again later or contact support.",
  DEFAULT: "An unexpected error occurred. Please try again.",
};

export function useLoginForm() {
  const history = useHistory();
  const { login } = useContext(AuthContext);
  const { setEnv } = useContext(EnvironmentContext);

  const [formData, setFormData] = useState<LoginFormData>(DEFAULT_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [generalError, setGeneralError] = useState<string>("");

  const clearErrors = () => {
    setErrors({});
    setGeneralError("");
  };

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear field-specific error
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Clear general error
    if (generalError) {
      setGeneralError("");
    }
  };

  const handleError = (error: any) => {
    console.error("Login error:", error);

    if (error?.response?.status === 401) {
      setGeneralError(ERROR_MESSAGES[401]);
    } else if (error?.response?.status === 422) {
      const validationErrors = error?.response?.data?.errors || {};
      setErrors(validationErrors);
    } else if (error?.response?.status === 404) {
      setGeneralError(ERROR_MESSAGES[404]);
    } else if (error?.response?.status >= 500) {
      setGeneralError(ERROR_MESSAGES[500]);
    } else if (error?.code === "NETWORK_ERROR" || !error?.response) {
      setGeneralError(ERROR_MESSAGES.NETWORK);
    } else {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        ERROR_MESSAGES.DEFAULT;
      setGeneralError(errorMessage);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    clearErrors();
    // TODO: Unhandled Promise Rejection: TypeError: "" cannot be parsed as a URL.
    const webUrl = formData.web.endsWith("/")
      ? formData.web.slice(0, -1)
      : formData.web;
    const url = new URL(webUrl).origin;

    try {
      const response = await axios.post(
        url + "/api/auth/login?office_addin=true",
        {
          email: formData.email,
          password: formData.password,
          web: url,
        }
      );
      const user = (response.data.data as any).user as User;
      await OfficeRuntime.storage.setItem("token", response.data.data.token);
      await OfficeRuntime.storage.getItem("token").then((res) => {
        login(user);
        setEnv(url);
        history.push("/");
      });
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldError = (field: string) => {
    return errors[field] || undefined;
  };

  return {
    errors,
    formData,
    generalError,
    getFieldError,
    handleInputChange,
    handleSubmit,
    isLoading,
  };
}
